import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Database, FileText, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [contactsRes, quotesRes] = await Promise.all([
        axios.get(`${API}/contact`, config),
        axios.get(`${API}/quote-requests`, config),
      ]);
      setContacts(contactsRes.data);
      setQuoteRequests(quotesRes.data);
    } catch (error) { toast.error('Failed to fetch data'); }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchData();
  }, [user, navigate, fetchData]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="badge mb-3">ADMIN CONTROL</span>
              <h1 className="text-4xl font-black text-foreground tracking-tight" data-testid="admin-dashboard-title">Admin Dashboard</h1>
              <p className="text-muted-foreground mt-2">Manage enquiries and website content</p>
            </div>
            <Link to="/admin/content-manager">
              <Button variant="outline" className="border-border/30 hover:border-primary/30 rounded-xl" data-testid="cms-button">
                <Database className="w-5 h-5 mr-2" /> Content Manager
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-card border-border/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }}>
                    <Package className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-foreground font-mono-spec">{quoteRequests.length}</p>
                    <p className="text-sm text-muted-foreground">Quote Requests</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/10">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-foreground font-mono-spec">{contacts.length}</p>
                    <p className="text-sm text-muted-foreground">Contact Inquiries</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary/10">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-foreground font-mono-spec">{quoteRequests.filter(q => q.status === 'pending').length}</p>
                    <p className="text-sm text-muted-foreground">Pending Quotes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="quotes" className="space-y-6">
            <TabsList className="bg-card border border-border/30">
              <TabsTrigger value="quotes" data-testid="quotes-tab">Quote Requests ({quoteRequests.length})</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="contacts-tab">Contact Inquiries ({contacts.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="quotes" className="space-y-4">
              {quoteRequests.length === 0 ? (
                <Card className="bg-card border-border/30"><CardContent className="p-12 text-center"><Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No quote requests yet.</p></CardContent></Card>
              ) : quoteRequests.map((quote, idx) => (
                <Card key={idx} className="bg-card border-border/30" data-testid={`quote-card-${idx}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-foreground">{quote.companyName} - {quote.fullName}</CardTitle>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${quote.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-primary/20 text-primary'}`}>{quote.status}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div><p className="text-muted-foreground">Email</p><p className="font-medium text-foreground">{quote.email}</p></div>
                      <div><p className="text-muted-foreground">Phone</p><p className="font-medium text-foreground">{quote.phone}</p></div>
                      <div><p className="text-muted-foreground">Dimensions</p><p className="font-medium text-foreground font-mono-spec">{quote.length} x {quote.breadth} x {quote.height} {quote.dimensionUnit || 'MM'}</p></div>
                      <div><p className="text-muted-foreground">Ply / MOQ</p><p className="font-medium text-foreground font-mono-spec">{quote.ply} Ply / {quote.moq}</p></div>
                    </div>
                    {quote.categoryName && <p className="text-xs text-muted-foreground mb-1">Category: <span className="text-primary">{quote.categoryName}</span></p>}
                    {quote.productName && <p className="text-xs text-muted-foreground mb-1">Product: <span className="text-primary">{quote.productName}</span></p>}
                    {quote.additionalNotes && <p className="text-sm text-foreground/80 mt-2 p-3 bg-background rounded-lg border border-border/30">{quote.additionalNotes}</p>}
                    <p className="text-xs text-muted-foreground mt-3 font-mono-spec">{new Date(quote.created_at).toLocaleString()}</p>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="contacts" className="space-y-4">
              {contacts.length === 0 ? (
                <Card className="bg-card border-border/30"><CardContent className="p-12 text-center"><Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No contact inquiries yet.</p></CardContent></Card>
              ) : contacts.map(contact => (
                <Card key={contact.id} className="bg-card border-border/30" data-testid={`contact-card-${contact.id}`}>
                  <CardHeader><CardTitle className="text-lg text-foreground">{contact.name}</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                      <div><p className="text-muted-foreground">Email</p><p className="font-medium text-foreground">{contact.email}</p></div>
                      {contact.phone && <div><p className="text-muted-foreground">Phone</p><p className="font-medium text-foreground">{contact.phone}</p></div>}
                      {contact.company && <div><p className="text-muted-foreground">Company</p><p className="font-medium text-foreground">{contact.company}</p></div>}
                    </div>
                    <div><p className="text-muted-foreground text-sm mb-2">Message</p><p className="text-foreground/80 p-3 bg-background rounded-lg border border-border/30">{contact.message}</p></div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      <Footer user={user} setUser={setUser} />
    </div>
  );
}
